import { curry, get } from 'lodash';
import React from 'react';
import { Query, Mutation } from 'react-apollo';

import { NAME as ASSET_VALIDATION_QUERY } from 'compositions/CaseAssetValidationPanel/withCaseAssetValidation/caseAssetValidationQuery';

import CaseInboundProgramPanelContext from '../CaseInboundProgramPanelContext';

import caseInboundProgramNumberQuery, {
  NAME as CASE_INBOUND_PROGRAM_NUMBER_QUERY,
} from './caseInboundProgramNumberQuery';
import updateCaseInboundProgramNumberIDMutation from './updateCaseInboundProgramNumberIDMutation';

const handleRequestCompletion = curry(
  (
    updateCaseInboundProgramPanelContext,
    inboundProgramNumberPath,
    customerNameDataPath,
    data,
  ) => {
    const inboundProgramNumberId = get(data, inboundProgramNumberPath);
    const inboundProgramSearchValue = get(data, customerNameDataPath, '');

    const newContext = { inboundProgramSearchValue };
    if (!inboundProgramNumberId) {
      newContext.inboundProgramLocationSearchValue = '';
      newContext.displayInboundProgramLocationSelector = false;
    }
    updateCaseInboundProgramPanelContext(newContext);
  },
);

export const buildMutationChild = curry(
  (WrappedComponent, componentProps, updateCaseInboundProgramNumberId) => {
    const {
      caseId,
      // remove these from the props passed to the WrappedComponent
      caseNumber,
      updateCaseInboundProgramPanelContext,
      ...rest
    } = componentProps;

    const onSelectInboundProgram = inboundProgramNumberId => {
      updateCaseInboundProgramNumberId({
        variables: { caseId, inboundProgramNumberId },
      });
    };

    const clearSelectedInboundProgram = () => {
      updateCaseInboundProgramNumberId({
        variables: { caseId, inboundProgramNumberId: null },
      });
    };

    return (
      <WrappedComponent
        {...rest}
        caseId={caseId}
        onSelectInboundProgram={onSelectInboundProgram}
        clearSelectedInboundProgram={clearSelectedInboundProgram}
      />
    );
  },
);

function buildWrappedComponentWithMutation(WrappedComponent, componentProps) {
  const { onRequestCompleted } = componentProps;

  return (
    <Mutation
      mutation={updateCaseInboundProgramNumberIDMutation}
      onCompleted={onRequestCompleted(
        'updateCase.case.inboundProgramNumber.id',
        'updateCase.case.inboundProgramNumber.customerName',
      )}
      refetch={[CASE_INBOUND_PROGRAM_NUMBER_QUERY, ASSET_VALIDATION_QUERY]}
    >
      {buildMutationChild(WrappedComponent, componentProps)}
    </Mutation>
  );
}

export const buildQueryChild = curry(
  (WrappedComponent, componentProps, queryProps) => {
    const selectedInboundProgram = get(
      queryProps,
      'data.case.inboundProgramNumber',
    );

    return buildWrappedComponentWithMutation(WrappedComponent, {
      ...componentProps,
      selectedInboundProgram,
    });
  },
);

function buildQueryVariables({ caseNumber }) {
  return { caseNumber };
}

export function buildWrappedComponentWithQuery(
  WrappedComponent,
  componentProps,
) {
  const { onRequestCompleted } = componentProps;

  return (
    <Query
      query={caseInboundProgramNumberQuery}
      variables={buildQueryVariables(componentProps)}
      onCompleted={onRequestCompleted(
        'case.inboundProgramNumber.id',
        'case.inboundProgramNumber.customerName',
      )}
      fetchPolicy="cache-and-network"
    >
      {buildQueryChild(WrappedComponent, componentProps)}
    </Query>
  );
}

export const buildConsumerChild = curry(
  (WrappedComponent, componentProps, consumerProps) => {
    const {
      caseId,
      caseNumber,
      inboundProgramSearchValue,
      updateCaseInboundProgramPanelContext,
    } = consumerProps;

    const onRequestCompleted = handleRequestCompletion(
      updateCaseInboundProgramPanelContext,
    );

    return buildWrappedComponentWithQuery(WrappedComponent, {
      ...componentProps,
      caseId,
      caseNumber,
      inboundProgramSearchValue,
      onRequestCompleted,
      updateCaseInboundProgramPanelContext,
    });
  },
);

const withInboundProgramSelect = WrappedComponent => componentProps => (
  <CaseInboundProgramPanelContext.Consumer>
    {buildConsumerChild(WrappedComponent, componentProps)}
  </CaseInboundProgramPanelContext.Consumer>
);

export default withInboundProgramSelect;



// WEBPACK FOOTER //
// ./src/compositions/CaseInboundProgramPanel/withInboundProgramSelect/index.js